import {useTranslation} from "react-i18next";
import React from "react";
import {
    alpha,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    DialogContentText,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Popover,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ContextMenuItemLabel from "../../general/menuItem/ContextMenuItemLabel";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import StandardDialogSlide from "../../general/dialog/StandardDialogSlide";
import TagCreatorComponent, {TagUpdaterComponent} from "./TagEditorComponent";
import {makeRequest} from "../../../services/requestService";
import {API_URL} from "../../../constants";
import {Shake} from "../../general/animation/Shake";
import {Boop} from "../../general/animation/Boop";
import InfoLabel from "../../TreeDataViewer/Annotation/Annotator/InfoLabel";
import PropTypes from "prop-types";

TagSetCardSkeleton.propTypes = {
    editable: PropTypes.bool
}

TagSetCardSkeleton.defaultProps = {
    editable: false
}

export function TagSetCardSkeleton({editable}) {
    return (
        <Card sx={{width: "100%"}} elevation={0}>
            <CardHeader
                title={
                    <Skeleton width="150px"/>
                }
                subheader={
                    <Skeleton width="200px"/>
                }
                action={editable &&
                    <IconButton disabled aria-label="settings">
                        <MoreVertIcon/>
                    </IconButton>
                }/>
            <CardContent>
                <Stack direction="row" spacing={1}>
                    <Skeleton sx={{borderRadius: "1em"}} variant="rectangular" height="30px" width="80px"/>
                    <Skeleton sx={{borderRadius: "1em"}} variant="rectangular" height="30px" width="80px"/>
                    <Skeleton sx={{borderRadius: "1em"}} variant="rectangular" height="30px" width="80px"/>
                </Stack>
            </CardContent>
        </Card>
    );
}

TagSetCard.propTypes = {
    labelSetID: PropTypes.number,
    onDelete: PropTypes.func,
    editable: PropTypes.bool
}

TagSetCard.defaultProps = {
    onDelete: () => null,
    editable: false
}

export default function TagSetCard(props) {
    const {labelSetID, onDelete, editable} = props;
    const {t} = useTranslation('common');
    const [openCreateTag, setOpenCreateTag] = React.useState(false);
    const [openUpdateTag, setOpenUpdateTag] = React.useState(false);
    const [openDelete, setOpenDelete] = React.useState(false);
    const [currentLabel, setCurrentLabel] = React.useState(undefined);
    const [isEditable, setIsEditable] = React.useState(false);
    const [collection, setCollection] = React.useState();
    const [loading, setLoading] = React.useState(true);
    const [updated, setUpdated] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const id = open && currentLabel ? `labelInfo-popover-${currentLabel.id}` : undefined;
    // Context menu state
    const initialState = {
        mouseX: null,
        mouseY: null,
    };
    const [stateMenu, setStateMenu] = React.useState(initialState);

    React.useEffect(() => {
        if (!loading)
            return;
        let timeoutId;
        if (labelSetID)
            makeRequest(`${API_URL}/label/set/${labelSetID}`).then((response) => {
                if (response.status === 200) {
                    setCollection(response.data);
                    setUpdated(false);
                    timeoutId = window.setTimeout(() => {
                        setLoading(false);
                    }, 200);
                }
            });
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [loading, labelSetID]);

    const submit = () => {
        makeRequest(`${API_URL}/label/set/${labelSetID}`, "PUT", collection).then((response) => {
            if (response.status === 200) {
                setLoading(true);
            }
        });
    }

    // Tags management
    const handleCreateTag = () => {
        setOpenCreateTag(false);
        setLoading(true);
    }

    const handleUpdateTag = () => {
        setOpenUpdateTag(false);
        setLoading(true);
    }

    const handleDeleteTag = (label) => {
        makeRequest(`${API_URL}/label/${label.id}`, "DELETE").then((response) => {
            if (response.status === 200) {
                setLoading(true);
            }
        });
    }

    // Tags collection management
    const handleMenu = (event) => {
        setStateMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
        });
    };

    const handleCloseMenu = () => {
        setStateMenu(initialState);
        if (updated && !isEditable)
            submit();
    };

    const toggleEditMode = () => {
        handleCloseMenu();
        if (isEditable) {
            setIsEditable(false);
            if (updated)
                submit();
        } else {
            setIsEditable(true);
        }
    }

    const handleChangeTitle = (event) => {
        if (!collection)
            return;
        setCollection(prevState => {
            let newState = {...prevState};
            newState.name = event.target.value;
            return newState;
        })
        setUpdated(true);
    }

    const handleChangeDescription = (event) => {
        if (!collection)
            return;
        setCollection(prevState => {
            let newState = {...prevState};
            newState.description = event.target.value;
            return newState;
        });
        setUpdated(true);
    }

    const togglePrivate = () => {
        if (!collection)
            return;
        setCollection(prevState => {
            let newState = {...prevState};
            newState.private = !prevState.private;
            return newState;
        });
        setUpdated(true);
    }

    const handleDelete = () => {
        makeRequest(`${API_URL}/label/set/${labelSetID}`, "DELETE").then((response) => {
            if (response.status === 200) {
                onDelete && onDelete();
            }
        });
    }

    const handleClick = (event, label) => {
        setCurrentLabel(label);
        if (isEditable)
            setOpenUpdateTag(true);
        else
            setAnchorEl(event.currentTarget);
    };

    const handleCloseLabelInfo = () => {
        setAnchorEl(null);
    };

    if (loading) return <TagSetCardSkeleton/>;

    return (
        <Card sx={{width: "100%", userSelect: "none"}} elevation={0}>
            <CardHeader
                title={
                    <>{isEditable
                        ? <TextField
                            value={collection.name}
                            size="small"
                            color="secondary"
                            label={t("main.pages.profile.annotation.set.card.title")}
                            fullWidth
                            onChange={handleChangeTitle}
                            sx={{pr: 1}}/>
                        : <Typography gutterBottom variant="h6">
                            {collection.name}
                        </Typography>
                    }</>
                }
                subheader={
                    <>{isEditable
                        ? <TextField
                            value={collection.description}
                            size="small"
                            color="secondary"
                            label={t("main.pages.profile.annotation.set.card.desc")}
                            margin="normal"
                            fullWidth
                            onChange={handleChangeDescription}
                            sx={{pr: 1}}/>
                        : <Typography variant="body2" color="text.secondary">
                            {collection.description}
                        </Typography>
                    }</>
                }
                action={editable &&
                    <IconButton onClick={handleMenu} aria-label="settings">
                        <MoreVertIcon/>
                    </IconButton>
                }/>
            <CardContent>
                {Array.isArray(collection.labels) && collection.labels.map((label) => (
                    <Shake key={`label-${label.id}`} disabled={!isEditable}>
                        <Chip
                            sx={{
                                "&.MuiChip-root": {
                                    color: theme => theme.palette.getContrastText(label.color),
                                    bgcolor: label.color ? label.color : "default",
                                    ":hover": {
                                        bgcolor: label.color ? alpha(label.color, 0.6) : "default",
                                    }
                                },
                                ".MuiChip-deleteIcon": {
                                    color: theme => theme.palette.getContrastText(label.color),
                                    ":hover": {
                                        color: theme => alpha(theme.palette.getContrastText(label.color), 0.6)
                                    }
                                },
                                m: 1
                            }}
                            label={label.label}
                            onClick={(event) => handleClick(event, label)}
                            onDelete={isEditable ? () => handleDeleteTag(label) : undefined}
                        />
                    </Shake>
                ))}
                {editable && <Boop rotation={45}>
                    <IconButton onClick={() => setOpenCreateTag(true)}>
                        <AddCircleOutlineIcon/>
                    </IconButton>
                </Boop>}
            </CardContent>
            <Menu
                open={stateMenu.mouseY !== null}
                onClose={handleCloseMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    stateMenu.mouseY !== null && stateMenu.mouseX !== null
                        ? {top: stateMenu.mouseY, left: stateMenu.mouseX}
                        : undefined
                }
            >
                <MenuItem onClick={toggleEditMode}>
                    {isEditable
                        ? <ContextMenuItemLabel icon={<SaveIcon fontSize="small"/>}>
                            {t("main.components.button.save")}
                        </ContextMenuItemLabel>
                        : <ContextMenuItemLabel icon={<EditIcon fontSize="small"/>}>
                            {t("main.components.button.edit")}
                        </ContextMenuItemLabel>
                    }
                </MenuItem>
                <MenuItem onClick={() => {
                    handleCloseMenu();
                    setOpenDelete(true);
                }}>
                    <ContextMenuItemLabel
                        icon={<DeleteForeverIcon fontSize="small"/>}
                    >
                        {t("main.components.button.delete")}
                    </ContextMenuItemLabel>
                </MenuItem>
                <Divider sx={{ml: 1, mr: 1}}/>
                <ContextMenuItemLabel
                    icon={<Switch
                        aria-label="collection-private"
                        size="small"
                        checked={collection.private}
                        onChange={() => togglePrivate()}/>}
                >
                    {t("main.components.button.private")}
                </ContextMenuItemLabel>
            </Menu>
            <StandardDialogSlide
                // Create label dialog
                open={openCreateTag}
                onClose={() => setOpenCreateTag(false)}
                actions={[
                    <Button
                        key="dialog-create-label-action1"
                        onClick={() => setOpenCreateTag(false)}
                        color="secondary">
                        {t("main.components.button.cancel")}
                    </Button>
                ]}
            >
                <TagCreatorComponent collectionId={collection.id} onSubmit={handleCreateTag}/>
            </StandardDialogSlide>
            <StandardDialogSlide
                // Update label dialog
                open={openUpdateTag}
                onClose={() => setOpenUpdateTag(false)}
                actions={[
                    <Button
                        key="dialog-create-label-action1"
                        onClick={() => setOpenUpdateTag(false)}
                        color="secondary">
                        {t("main.components.button.cancel")}
                    </Button>
                ]}
            >
                {currentLabel && <TagUpdaterComponent label={currentLabel} onSubmit={handleUpdateTag}/>}
            </StandardDialogSlide>
            <StandardDialogSlide
                // delete labels collection dialog
                open={openDelete}
                onClose={() => setOpenDelete(false)}
                title={t("main.pages.profile.annotation.set.delete.title")}
                actions={[
                    <Button key="dialog-delete-action1" color="secondary" onClick={() => setOpenDelete(false)}>
                        {t("main.components.button.cancel")}
                    </Button>,
                    <Button key="dialog-delete-action2" color="error" onClick={() => {
                        setOpenDelete(false);
                        handleDelete();
                    }}>
                        {t("main.components.button.delete")}
                    </Button>
                ]}
            >
                <DialogContentText>
                    {t("main.pages.profile.annotation.set.delete.desc")}
                </DialogContentText>
            </StandardDialogSlide>
            <Popover
                id={id}
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseLabelInfo}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <InfoLabel label={currentLabel}/>
            </Popover>
        </Card>
    );
}